// ═══════════════════════════════════════════════════════════════════════
// Paramètres › Réglementation (Lot 1, Lot 2A) : versions réglementaires datées (« packs »), par exemple
// « Sénégal — Temps & Congés — V1 ». Un pack regroupe des règles de loi, de convention ou d'accord, chacune
// à son niveau, toutes à la même date d'effet ; il est simulé, puis validé EN BLOC par une autre personne.
// Une nouvelle loi = une nouvelle version du pack : la précédente reste intacte pour les périodes passées.
// Le détail d'un pack : PackReglementairePage.
// ═══════════════════════════════════════════════════════════════════════
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Eye, Landmark } from 'lucide-react';
import Layout from '../../components/Layout';
import { EnTetePage, Carte, Bouton, BandeauInfo, EtatVide, Squelette, formaterDate } from '../../design/composants';
import { useChargement, Fenetre, Champ, PiedFenetre } from '../temps/reglagesCommun';
import {
  lireCfg, ecrireCfg, messageErreur, NavParametres, NIVEAUX, NATURES_SOURCE_PACK, BadgeStatutRegle,
} from './configurationCommun';

const DOMAINES_PACK = [['TEMPS', 'Temps et pointage'], ['HEURES_SUP', 'Heures supplémentaires'], ['CONGES', 'Congés et absences'], ['RETENUES', 'Retenues sur salaire']];

function FenetreNouveauPack({ onFermer, onTermine }) {
  const [f, setF] = useState({ code: '', libelle: '', niveau: 'LOI', pays: 'SN', domaines: [], date_debut: '', date_fin: '', source_nature: 'LOI', source_reference: '',
    date_publication: '', textes: '', commentaire: '', motif: '' });
  const [erreur, setErreur] = useState('');
  const maj = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Fenetre titre="Nouvelle version réglementaire" Icone={Landmark} onFermer={onFermer}>
      {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
      <Champ libelle="Identifiant" requis aide="Le même identifiant pour toutes les versions d'un même ensemble (exemple : SENEGAL-TEMPS-CONGES)."><input className="rh-champ-saisie" value={f.code} onChange={maj('code')} /></Champ>
      <Champ libelle="Libellé" requis><input className="rh-champ-saisie" value={f.libelle} onChange={maj('libelle')} placeholder="Sénégal — Temps & Congés" /></Champ>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
        <Champ libelle="Niveau" requis><select className="rh-champ-saisie" value={f.niveau} onChange={maj('niveau')}>{NIVEAUX.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></Champ>
        <Champ libelle="Date d'effet" requis><input type="date" className="rh-champ-saisie" value={f.date_debut} onChange={maj('date_debut')} /></Champ>
        <Champ libelle="Date de fin"><input type="date" className="rh-champ-saisie" value={f.date_fin} onChange={maj('date_fin')} /></Champ>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
        <Champ libelle="Nature de la source" requis><select className="rh-champ-saisie" value={f.source_nature} onChange={maj('source_nature')}>{Object.entries(NATURES_SOURCE_PACK).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></Champ>
        <Champ libelle="Date de publication"><input type="date" className="rh-champ-saisie" value={f.date_publication} onChange={maj('date_publication')} /></Champ>
      </div>
      <Champ libelle="Source principale"><input className="rh-champ-saisie" value={f.source_reference} onChange={maj('source_reference')} placeholder="Loi n° 2026-18 du 3 septembre 2026, JO n° 7931" /></Champ>
      <Champ libelle="Textes complémentaires (un par ligne)"><textarea className="rh-champ-saisie" rows={3} value={f.textes} onChange={maj('textes')} /></Champ>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
        <Champ libelle="Pays (code)"><input className="rh-champ-saisie" value={f.pays} maxLength={2} onChange={(e) => setF({ ...f, pays: e.target.value.toUpperCase() })} /></Champ>
        <fieldset style={{ border: 0, padding: 0, margin: '0 0 12px' }}>
          <legend style={{ fontSize: 13, fontWeight: 500, color: 'var(--sg-texte-2)', marginBottom: 4 }}>Domaines</legend>
          {DOMAINES_PACK.map(([k, l]) => (
            <label key={k} style={{ display: 'flex', gap: 6, fontSize: 14 }}>
              <input type="checkbox" checked={f.domaines.includes(k)} onChange={(e) => setF({ ...f, domaines: e.target.checked ? [...f.domaines, k] : f.domaines.filter((x) => x !== k) })} />{l}
            </label>
          ))}
        </fieldset>
      </div>
      <Champ libelle="Commentaire"><textarea className="rh-champ-saisie" rows={2} value={f.commentaire} onChange={maj('commentaire')} /></Champ>
      <Champ libelle="Motif" requis><textarea className="rh-champ-saisie" rows={2} value={f.motif} onChange={maj('motif')} /></Champ>
      <PiedFenetre>
        <Bouton onClick={onFermer}>Retour</Bouton>
        <Bouton variante="primaire" Icone={Plus} disabled={!f.code || !f.libelle || !f.date_debut || f.motif.trim().length < 3}
          onClick={async () => {
            try {
              const { textes, ...corps } = f;
              const p = await ecrireCfg('post', '/packs', { ...corps, date_fin: f.date_fin || null, date_publication: f.date_publication || null,
                references: textes.split('\n').map((t) => t.trim()).filter(Boolean) });
              onTermine(p);
            } catch (e) { setErreur(messageErreur(e)); }
          }}>Créer</Bouton>
      </PiedFenetre>
    </Fenetre>
  );
}

export default function ReglementationPage() {
  const dom = useChargement(() => lireCfg('/domaines'), []);
  const { donnees, erreur, recharger } = useChargement(() => lireCfg('/packs'), []);
  const [fenetre, setFenetre] = useState(null);
  const navigate = useNavigate();
  const droits = dom.donnees?.droits?.domaines?.REGLEMENTATION;
  return (
    <Layout>
      <div className="rh-page">
        <EnTetePage fil={[{ libelle: 'Paramètres', vers: '/parametres' }, { libelle: 'Réglementation' }]} titre="Réglementation"
          sousTitre="Versions réglementaires datées : quand un texte change, on prépare une nouvelle version ; la précédente continue de s'appliquer aux périodes passées."
          actions={droits?.preparer && <Bouton variante="primaire" Icone={Plus} onClick={() => setFenetre({ type: 'nouveau' })}>Nouvelle version réglementaire</Bouton>} />
        <NavParametres actif="reglementation" />
        {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
        {!donnees && !erreur && <Squelette hauteur={140} />}
        {donnees && donnees.length === 0 && <EtatVide titre="Aucune version réglementaire" texte="Les règles de loi, de convention ou d'accord peuvent être regroupées en versions datées." />}
        {donnees && donnees.length > 0 && (
          <Carte titre="Versions réglementaires">
            <div className="rh-conteneur-tableau"><table className="rh-tableau">
              <thead><tr><th>Ensemble</th><th>Version</th><th>Niveau principal</th><th>Date d'effet</th><th>Source</th><th>Statut</th><th aria-label="Action" /></tr></thead>
              <tbody>
                {donnees.map((p) => (
                  <tr key={p.id}>
                    <td>{p.libelle}</td><td>V{p.version}</td><td>{p.niveau_libelle}</td><td>{formaterDate(p.date_debut)}</td>
                    <td>{NATURES_SOURCE_PACK[p.source_nature] || p.source_nature}</td><td><BadgeStatutRegle statut={p.statut} /></td>
                    <td><Link to={`/parametres/reglementation/${p.id}`} className="rh-btn rh-btn-petit" style={{ textDecoration: 'none' }}><Eye size={14} aria-hidden="true" /> Voir</Link></td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          </Carte>
        )}
        {fenetre?.type === 'nouveau' && <FenetreNouveauPack onFermer={() => setFenetre(null)} onTermine={(p) => { recharger(); navigate(`/parametres/reglementation/${p.id}`); }} />}
      </div>
    </Layout>
  );
}
